import React, { useState } from 'react';
import { Button, Dialog } from '@mui/material';
import { entityNameMinimumLength, validateEmail } from 'const/regex';
import { User } from '@restore/services-lib';
import { CreateUserInput, useUsersContext } from 'context/users.context';
import { FormFieldInterface } from 'components/Form/Form.types';
import Form from 'components/Form/Form';
import UserGroupsSelectDropdown from './UserGroupsSelectDropdown';
import { DropdownOption } from 'components/DropdownSelect/dropdown.types';
import TagsMultiSelectDropdown from 'components/DropdownSelect/MultiSelectDropdown/Usage/TagsMultiSelectDropdown';
import { useAuthContext } from 'context/auth.context';
import { hasPermissions } from 'config/ability';

const defaultNewUser: CreateUserInput = {
  firstName: '',
  lastName: '',
  email: '',
  userGroupIds: [],
  tagIds: [],
};

const CreateUser = () => {
  const [show, setShow] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const { addUser } = useUsersContext();
  const [newUser, setNewUser] = useState<CreateUserInput>(defaultNewUser);
  const [selectedOptions, setSelectedOptions] = useState<DropdownOption[]>();
  const { ability } = useAuthContext();

  // Return null if user does not have 'Create' permissions
  if (!hasPermissions(ability, 'create', 'User')) {
    return null;
  }

  const handleAddUser = async () => {
    setLoading(true);
    await addUser(newUser);
    setNewUser(defaultNewUser);
    setShow(false);
    setLoading(false);
  };

  const handleUpdateField = (value: string, key: keyof User) =>
    setNewUser({ ...newUser, [key]: value });

  const handleUpdateUserUserGroups = (updateUserGroups: DropdownOption[]) => {
    setSelectedOptions(updateUserGroups);
    setNewUser({
      ...newUser,
      userGroupIds: updateUserGroups.map((store) => Number(store.id)),
    });
  };

  const handleUpdateTags = (updateTags: DropdownOption[]) => {
    setNewUser({
      ...newUser,
      tagIds: updateTags.map((tag) => Number(tag.id)),
    });
  };

  const fields: FormFieldInterface[] = [
    {
      label: 'First Name',
      value: newUser.firstName,
      onChange: (value) => handleUpdateField(value, 'firstName'),
      fullWidth: true,
      requiredMinimumLength: entityNameMinimumLength,
    },
    {
      label: 'Last Name',
      value: newUser.lastName,
      onChange: (value) => handleUpdateField(value, 'lastName'),
      fullWidth: true,
      requiredMinimumLength: entityNameMinimumLength,
    },
    {
      label: 'Email',
      value: newUser.email,
      onChange: (value) => handleUpdateField(value, 'email'),
      fullWidth: true,
      requiredMinimumLength: 1,
    },
    {
      label: 'User Groups',
      onChange: () => {},
      customField: (
        <UserGroupsSelectDropdown
          user={newUser}
          onChange={handleUpdateUserUserGroups}
          selectedOptionsOverride={selectedOptions}
        />
      ),
    },
    {
      label: 'Tags',
      onChange: () => {},
      customField: <TagsMultiSelectDropdown onChange={handleUpdateTags} />,
    },
  ];

  const isFormDisabled = !validateEmail(newUser.email);

  return (
    <>
      <Button variant='contained' color='secondary' onClick={() => setShow(true)}>
        Create New User
      </Button>
      <Dialog open={show} onClose={() => setShow(false)}>
        <Form
          fields={fields}
          title='Create New User'
          onSubmit={handleAddUser}
          submitting={loading}
          disabled={isFormDisabled}
          buttonText='Create User'
        />
      </Dialog>
    </>
  );
};

export default CreateUser;
